import {initContract} from '@ts-rest/core';
import {DB_SCHEMAS} from '../db/schemaExports';
import {z} from 'zod';
import {paginationQuerySchema, paginatedResponseSchema} from '../utils/pagination';

const c = initContract();

const sourceTypeSchema = z.enum(['file', 'webpage']);

const createSourceSchema = DB_SCHEMAS.createSource
  .pick({
    name: true,
    type: true,
    uri: true,
    kbId: true
  })
  .extend({
    type: sourceTypeSchema,
    name: DB_SCHEMAS.createSource.shape.name.optional()
  });

const updateSourceSchema = DB_SCHEMAS.updateSource.extend({
  type: sourceTypeSchema
});

export const sourcesContract = c.router(
  {
    create: {
      method: 'POST',
      path: '/',
      responses: {
        201: DB_SCHEMAS.selectSource,
        403: z.object({message: z.string()}),
        404: z.object({message: z.string()})
      },
      body: createSourceSchema,
      summary: 'Create a source'
    },
    get: {
      method: 'GET',
      path: '/:id',
      pathParams: z.object({
        id: z.string()
      }),
      responses: {
        200: DB_SCHEMAS.selectSource,
        403: z.object({message: z.string()}),
        404: z.object({message: z.string()})
      },
      summary: 'Get a source'
    },
    update: {
      method: 'PUT',
      path: '/:id',
      pathParams: z.object({
        id: z.string()
      }),
      body: updateSourceSchema,
      responses: {
        200: z.array(DB_SCHEMAS.selectSource),
        403: z.object({message: z.string()}),
        404: z.object({message: z.string()})
      },
      summary: 'Update a source'
    },
    download: {
      method: 'GET',
      path: '/:id/download',
      pathParams: z.object({
        id: z.string()
      }),
      responses: {
        200: z.instanceof(ReadableStream),
        404: z.object({error: z.string()}),
        403: z.object({error: z.string()})
      },
      summary: 'Download a file source'
    },
    getMarkdown: {
      method: 'GET',
      path: '/:id/markdown',
      pathParams: z.object({
        id: z.string()
      }),
      responses: {
        200: z.object({markdown: z.string()}),
        403: z.object({message: z.string()}),
        404: z.object({message: z.string()})
      },
      summary: 'Get the rendered Markdown for a source'
    },
    delete: {
      method: 'DELETE',
      path: '/:id',
      pathParams: z.object({
        id: z.string()
      }),
      responses: {
        200: z.array(DB_SCHEMAS.selectSource),
        403: z.object({message: z.string()}),
        404: z.object({message: z.string()})
      },
      summary: 'Delete a source'
    },
    list: {
      method: 'GET',
      path: '/',
      query: z
        .object({kbId: z.string().optional(), sourceIds: z.array(z.string()).optional()})
        .merge(paginationQuerySchema)
        .extend({
          sortBy: z.enum(['name', 'type', 'createdAt', 'numDocuments']).optional()
        })
        .refine((data) => data.kbId || (data.sourceIds && data.sourceIds.length > 0), {
          message: 'At least one of kbId or sourceIds must be provided.'
        }),
      responses: {
        200: paginatedResponseSchema(DB_SCHEMAS.selectSource),
        403: z.object({message: z.string()}),
        404: z.object({message: z.string()})
      },
      summary: 'List sources'
    }
  },
  {
    pathPrefix: '/sources'
  }
);
